import { Component, Input, Output, EventEmitter, OnInit, HostListener, inject, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, map, takeUntil } from 'rxjs/operators';
import { NavigationItem } from '../../../utils/interfaces';
import { LogError, UserInfo } from '@memberjunction/core';
import { ActiveUserService } from 'src/app/services/active-user.service';
import { RoleGuard } from 'src/app/guards/role.guard';
import { ImpersonationService } from 'src/app/services/impersonation.service';
import { AnnouncementService } from 'src/app/services/announcement.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { Observable, Subject } from 'rxjs';

@Component({
  selector: 'acs-navigation',
  templateUrl: './acs-navigation.component.html',
  styleUrls: ['./acs-navigation.component.scss']
})
export class AcsNavigationComponent implements OnInit, OnDestroy {
  private router = inject(Router);

  @Input() items: NavigationItem[] = [];
  @Input() collapsed: boolean = false;
  @Input() showUserSection: boolean = true;
  @Input() logo: string = 'ACS_small_rgb.jpg';
  @Input() logoText: string = 'ACS Portal';

  @Output() collapseToggle = new EventEmitter<boolean>();
  @Output() userMenuClick = new EventEmitter<string>();
  @Output() navigationReady = new EventEmitter<boolean>();

  expandedItems: Set<string> = new Set();
  hoverExpanded: boolean = false;
  isMobile: boolean = false;
  currentRoute: string = '';
  user !: UserInfo;
  private lastUserId: string | null = null;
  
  // Navigation loading state
  isNavigationReady: boolean = false;
  
  // Impersonation tracking
  isImpersonating$: Observable<boolean>;
  isImpersonating: boolean = false;
  
  // Announcement tracking
  unreadAnnouncementCount$: Observable<number>;
  private destroy$ = new Subject<void>();

  // Default navigation items with routes
  defaultItems: NavigationItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: '📊',
      route: '/dashboard'
    },
    {
      id: 'chapters',
      label: 'Chapters',
      icon: '🏥',
      children: [
        { id: 'chapters-list', label: 'All Chapters', icon: '📋', route: '/chapters' }
      ]
    },
    {
      id: 'members',
      label: 'Members',
      icon: '👥',
      children: [
        { id: 'members-roster', label: 'Member Roster', icon: '👤', route: '/members' },
        { id: 'chapter-officers', label: 'Officers', icon: '👔', route: '/chapter-officers' }
      ]
    },
    {
      id: 'dues',
      label: 'Dues Management',
      icon: '💰',
      children: [
        { id: 'chapters-dues', label: 'Chapter Dues', icon: '📈', route: '/chapter-dues' },
        { id: 'member-chapters-dues', label: 'Member Chapter Dues', icon: '📊', route: '/member-chapter-dues' }
      ]
    },
    {
      id: 'announcements',
      label: 'Announcements',
      icon: '📢',
      children: [
        { id: 'announcements-view', label: 'View Announcements', icon: '👁️', route: '/announcements' },
        { id: 'announcements-manage', label: 'Manage Announcements', icon: '⚙️', route: '/announcements/manage' }
      ]
    },
    {
      id: 'documents',
      label: 'My Documents',
      icon: '📄',
      route: '/documents'
    },
    {
      id: 'admin',
      label: 'Administration',
      icon: '⚙️',
      permissions: ['admin'],
      children: [
        { id: 'admin-users', label: 'User Management', icon: '👤', route: '/admin/users' },
        { id: 'admin-impersonation-logs', label: 'Impersonation Logs', icon: '🔐', route: '/admin/impersonation-logs' },
        { id: 'admin-portal-logs', label: 'Portal Access Logs', icon: '📊', route: '/admin/portal-logs' },
        { id: 'admin-themes', label: 'Theme Configuration', icon: '🎨', route: '/admin/themes' }
      ]
    }
  ];

  constructor(
    private activeUserService: ActiveUserService,
    private roleGuard: RoleGuard,
    private impersonationService: ImpersonationService,
    private announcementService: AnnouncementService,
    private userAuthService: UserAuthService
  ) {
    // Set up impersonation status observable
    this.isImpersonating$ = this.impersonationService.getSessionStatus().pipe(
      map(status => status.isImpersonating)
    );
    
    // Subscribe to impersonation status for template usage
    this.isImpersonating$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(status => {
      this.isImpersonating = status;
    });
    
    // Set up announcement unread count observable
    this.unreadAnnouncementCount$ = this.announcementService.unreadCount$;
    
    // Subscribe to unread count changes to update navigation badge
    this.unreadAnnouncementCount$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(count => {
      this.updateAnnouncementBadge(count);
    });
  }

  ngOnInit(): void {
    // Use default items if none provided
    if (this.items.length === 0) {
      this.items = this.defaultItems;
    }

    // Check if mobile
    this.checkMobile();

    // Subscribe to active user changes (including impersonation changes)
    this.activeUserService.activeUser$.pipe(
      filter(userInfo => userInfo !== null),
      takeUntil(this.destroy$)
    ).subscribe(async (userInfo) => {
      if (!userInfo) return; // Extra safety check
      
      const newUserId = userInfo.activeUser.ID;
      
      // Always filter on the first user load or when user actually changes
      if (this.lastUserId !== newUserId) {
        // For user switches (like ending impersonation), temporarily hide navigation while filtering
        const isUserSwitch = this.lastUserId !== null;

        this.user = userInfo.activeUser;
        this.lastUserId = newUserId;

        // Only clear role guard cache when user actually switches (not on initial load)
        if (isUserSwitch) {
          this.roleGuard.onUserContextChanged();
          this.isNavigationReady = false;
        }

        // Reset navigation items to default before filtering with new user permissions
        this.items = [...this.defaultItems];

        await this.filterNavigationItems();
        
        // Update announcement service with new user context
        this.announcementService.updateUserContext(userInfo.activeUser);
      }
    });

    // Fallback: If no user data comes through after 3 seconds, mark as ready anyway
    setTimeout(() => {
      if (!this.isNavigationReady) {
        this.isNavigationReady = true;
        this.navigationReady.emit(true);
      }
    }, 3000);

    // Subscribe to router events to track active route
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      this.currentRoute = event.urlAfterRedirects;
      this.expandActiveParent();
    });

    // Set initial route
    this.currentRoute = this.router.url;
    this.expandActiveParent();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkMobile();
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth < 768;
    if (this.isMobile) {
      this.collapsed = true;
    }
  }

  /**
   * Auto-expand parent items if child is active
   */
  private expandActiveParent(): void {
    this.items.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => 
          this.isRouteActive(child.route || '')
        );
        if (hasActiveChild) {
          this.expandedItems.add(item.id);
        }
      }
    });
  }

  /**
   * Toggle navigation collapse state
   */
  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    this.collapseToggle.emit(this.collapsed);
  }

  /**
   * Toggle submenu expansion
   */
  toggleSubmenu(item: NavigationItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (this.expandedItems.has(item.id)) {
      this.expandedItems.delete(item.id);
    } else {
      this.expandedItems.add(item.id);
    }
  }

  /**
   * Check if submenu is expanded
   */
  isExpanded(itemId: string): boolean {
    return this.expandedItems.has(itemId);
  }

  /**
   * Check if route is currently active
   */
  isRouteActive(route: string): boolean {
    if (!route) return false;
    
    // Check exact match first
    if (this.currentRoute === route) return true;
    
    // Check if current route starts with item route (for nested routes)
    return this.currentRoute.startsWith(route + '/');
  }

  /**
   * Check if item is active (matches original component)
   */
  isActive(item: NavigationItem): boolean {
    // Check if main item is active
    if (item.route && this.isRouteActive(item.route)) {
      return true;
    }
    
    // Check if any child is active
    if (item.children) {
      return item.children.some(child => this.isActive(child));
    }
    
    return false;
  }

  /**
   * Check if item or any of its children are active
   */
  isItemActive(item: NavigationItem): boolean {
    return this.isActive(item);
  }

  /**
   * Navigate to route programmatically
   */
  navigateTo(route: string): void {
    if (route) {
      this.router.navigate([route]);
      
      // Collapse navigation on mobile after navigation
      if (this.isMobile) {
        this.collapsed = true;
        this.collapseToggle.emit(this.collapsed);
      }
    }
  }

  /**
   * Handle navigation item click
   */
  onItemClick(item: NavigationItem, event: Event): void {
    // If item has children but no route, just toggle submenu
    if (item.children && item.children.length > 0 && !item.route) {
      this.toggleSubmenu(item, event);
    }
    // Otherwise navigate if route exists
    else if (item.route) {
      this.navigateTo(item.route);
    }
  }

  /**
   * Get the appropriate user action based on impersonation status
   */
  getUserAction(): string {
    return this.isImpersonating ? 'endImpersonation' : 'logout';
  }

  /**
   * Get the appropriate aria label for the user action button
   */
  getUserActionLabel(): string {
    return this.isImpersonating ? 'End impersonation' : 'Sign out';
  }

  /**
   * Get the appropriate icon for the user action button
   */
  getUserActionIcon(): string {
    return this.isImpersonating ? '👤' : '🚪';
  }

  /**
   * Handle user menu actions
   */
  async onUserMenuAction(action: string): Promise<void> {
    // Handle impersonation-specific actions locally
    if (action === 'endImpersonation') {
      try {
        await this.impersonationService.endCurrentSession('manual');
        // Don't emit to parent - we handled it locally
      } catch (error) {
        console.error('Error ending impersonation:', error);
      }
      return;
    }

    // Emit event for parent to handle other actions
    this.userMenuClick.emit(action);
    
    // Handle navigation for user menu items
    switch(action) {
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      case 'settings':
        this.router.navigate(['/settings']);
        break;
      case 'logout':
        // Emit event for parent to handle logout
        break;
    }
  }

  /**
   * Handle mouse events for hover expansion
   */
  onMouseEnter(): void {
    if (this.collapsed && !this.isMobile) {
      this.hoverExpanded = true;
    }
  }

  onMouseLeave(): void {
    if (this.collapsed && !this.isMobile) {
      this.hoverExpanded = false;
    }
  }

  /**
   * Get CSS classes for navigation container
   */
  get navigationClass(): string {
    const classes = ['acs-navigation'];
    if (this.collapsed && !this.hoverExpanded) classes.push('acs-navigation--collapsed');
    if (this.hoverExpanded) classes.push('acs-navigation--hover-expanded');
    if (this.isMobile) classes.push('acs-navigation--mobile');
    return classes.join(' ');
  }

  /**
   * Check if user has permission to see menu item
   */
  async hasPermission(item: NavigationItem): Promise<boolean> {
    // Check if user has "No Access" role first - this applies to ALL items
    try {
      const userRole = await this.roleGuard.getUserRole();
      if (userRole === 'No Access') {
        // Users with "No Access" role should not see any navigation items
        return false;
      }
    } catch (error) {
      LogError('Error checking user role:', undefined, error);
    }

    if (!item.route) {
      return true;
    }

    // Check if auth info is available before checking permissions
    const personId = this.userAuthService.getPersonId();
    const auraUniqueId = this.userAuthService.getAuraUniqueId();

    if (!personId || !auraUniqueId) {
      // Auth info not yet available, show the item temporarily
      // The navigation will be filtered again once auth info is available
      return true;
    }

    try {
      const hasAccess = await this.roleGuard.canAccessResource(item.route);
      return hasAccess;
    } catch (error) {
      LogError(`Permission check failed for route: ${item.route}`, undefined, error);
      // Default to showing the item if permission check fails
      return true;
    }
  }

  /**
   * Wait for auth info to be available
   */
  private async waitForAuthInfo(maxWaitMs: number = 5000): Promise<boolean> {
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
      const personId = this.userAuthService.getPersonId();
      const auraUniqueId = this.userAuthService.getAuraUniqueId();

      if (personId && auraUniqueId) {
        return true;
      }

      // Wait 100ms before checking again
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return false;
  }

  /**
   * Filter navigation items based on user permissions
   */
  private async filterNavigationItems(): Promise<void> {
    try {
      // Wait for auth info to be available before filtering
      const authInfoAvailable = await this.waitForAuthInfo();

      if (!authInfoAvailable) {
        // Even if auth info isn't available, try to check for No Access role
        try {
          const userRole = await this.roleGuard.getUserRole();
          if (userRole === 'No Access') {
            this.items = []; // Hide all navigation items
            this.isNavigationReady = true;
            this.navigationReady.emit(true);
            return;
          }
        } catch (error) {
          LogError('Error checking user role during auth info wait:', undefined, error);
        }
        // If not No Access role and auth info isn't available, show all items
        // This ensures navigation doesn't stay empty for valid users
      }

      const filteredItems = await this.filterItemsRecursive(this.items);
      this.items = filteredItems;

      // Reapply announcement badges after filtering
      const currentUnreadCount = this.announcementService.getCurrentUnreadCount();
      this.updateAnnouncementBadge(currentUnreadCount);

      // Mark navigation as ready after filtering is complete
      if (!this.isNavigationReady) {
        this.isNavigationReady = true;
        this.navigationReady.emit(true);
      }
    } catch (error) {
      LogError('Error during navigation filtering:', undefined, error);
      // Even if filtering fails, mark as ready to prevent infinite loading
      if (!this.isNavigationReady) {
        this.isNavigationReady = true;
        this.navigationReady.emit(true);
      }
    }
  }

  /**
   * Recursively filter navigation items and their children based on permissions
   */
  private async filterItemsRecursive(items: NavigationItem[]): Promise<NavigationItem[]> {
    const filteredItems: NavigationItem[] = [];

    for (const item of items) {
      let shouldInclude = await this.hasPermission(item);

      if (item.children && item.children.length > 0) {
        const filteredChildren = await this.filterItemsRecursive(item.children);
        
        if (filteredChildren.length > 0) {
          filteredItems.push({
            ...item,
            children: filteredChildren
          });
        } else if (!item.route) {
          // If parent has no route and no visible children, hide the parent
          shouldInclude = false;
        }
      } else if (shouldInclude) {
        filteredItems.push(item);
      }
    }

    return filteredItems;
  }

  /**
   * Update the announcement navigation item badge with unread count
   */
  private updateAnnouncementBadge(count: number): void {
    const updateItemBadge = (items: NavigationItem[]): void => {
      items.forEach(item => {
        // Update main announcements item
        if (item.id === 'announcements') {
          item.badge = count > 0 ? count.toString() : undefined;
        }
        // Also check children for "View Announcements"
        if (item.children) {
          item.children.forEach(child => {
            if (child.id === 'announcements-view') {
              child.badge = count > 0 ? count.toString() : undefined;
            }
          });
        }
      });
    };

    updateItemBadge(this.defaultItems);
    if (this.items && this.items.length > 0) {
      updateItemBadge(this.items);
    }
  }

  /**
   * Component cleanup
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}